import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { CalendlyCallie } from '@calendly/ui/components/glyphs/CalendlyCallie'
import { Spotlight } from '@calendly/ui/components/spotlight'
import { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { isAttachRecapContextEnabled, mergeRecapPanePropsFromPathname } from 'features/agent_hub/utils/recapContext'
import { MEETING_RECAP_TOUR, useURLParams } from 'features/meeting_intelligence'
import { useRightRail } from 'features/right_rail'
import { useTrack, useTrackOnce, TRACKING_EVENT_NAMES } from 'hooks/analytics_events'
import { useMeetingRecapViewed } from 'hooks/useMeetingRecapViewed'
import { isCookiesAllowedSelector } from 'selectors/gdpr'
import {
  SPOTLIGHT_TOUR_FLOW_ID,
  SPOTLIGHT_TOUR_PRIORITY,
} from 'shared/constants/spotlight_tours'

import { AI_CHAT_DISCOVERY_NAVIGATION_LOCATION_TOP_RIGHT } from './constants'
import {
  clearCallieOnboardingFromUrlIfPresent,
  clearMeetingsSelectionFromUrlIfPresent,
  closeAiChatRailPane,
  openAiChatRailPane,
} from './utils'

const MEETING_RECAP_TOUR_STEP_ONE_SHOWN = {
  name: 'Tooltip Shown',
  properties: {
    tooltip_name: 'ask_anything_about_your_meetings',
    tooltip_flow: 'meeting_recap_tour',
    tooltip_location: AI_CHAT_DISCOVERY_NAVIGATION_LOCATION_TOP_RIGHT,
  },
}

const MEETING_RECAP_TOUR_STEP_ONE_CLICKED = {
  name: 'Tooltip Clicked',
  properties: {
    tooltip_name: 'ask_anything_about_your_meetings',
    tooltip_flow: 'meeting_recap_tour',
    tooltip_location: AI_CHAT_DISCOVERY_NAVIGATION_LOCATION_TOP_RIGHT,
    action: 'tooltip_clicked',
  },
}

const MEETING_RECAP_TOUR_STEP_ONE_DISMISSED = {
  name: 'Tooltip Clicked',
  properties: {
    tooltip_name: 'ask_anything_about_your_meetings',
    tooltip_flow: 'meeting_recap_tour',
    tooltip_location: AI_CHAT_DISCOVERY_NAVIGATION_LOCATION_TOP_RIGHT,
    action: 'dismissed',
  },
}

/**
 * Ask Callie entry for MI-enabled users (legacy header + app bar).
 * Only mount from subtrees where {@link useAiChatDiscoveryMiEligible} is already true.
 *
 * @param {Object} props
 * @param {'string' | 'app_bar'} props.labelVariant
 * @param {'small' | undefined} [props.size]
 * @param {string} [props['data-testid']]
 */
export const AiChatDiscoveryTopButton = ({
  labelVariant,
  size,
  'data-testid': dataTestId,
}) => {
  const track = useTrack()
  const dispatch = useDispatch()
  const { search, pathname } = useLocation()
  const { openOrReplace, close, current } = useRightRail()
  const isRecapIntegrationEnabled = useFlag('ask_callie_recaps_page_integration')
  const functionalCookiesAllowed = useSelector(isCookiesAllowedSelector('functional'))
  const { findParam, replaceParam, removeParam } = useURLParams()
  const { markMeetingRecapAsViewed } = useMeetingRecapViewed()
  const buttonRef = useRef(null)

  // Require functional cookies so Tooltip Shown matches a visible Ask Callie + spotlight.
  const showMeetingRecapTourStep1 = (
    isRecapIntegrationEnabled
    && functionalCookiesAllowed
    && Boolean(findParam(MEETING_RECAP_TOUR.STEP_ONE))
  )

  useTrackOnce(showMeetingRecapTourStep1, MEETING_RECAP_TOUR_STEP_ONE_SHOWN)

  const handleTourContinue = useCallback(() => {
    track(MEETING_RECAP_TOUR_STEP_ONE_CLICKED)
    replaceParam(MEETING_RECAP_TOUR.STEP_TWO, MEETING_RECAP_TOUR.STEP_ONE)
  }, [replaceParam, track])

  const handleTourDismiss = useCallback(() => {
    track(MEETING_RECAP_TOUR_STEP_ONE_DISMISSED)
    markMeetingRecapAsViewed()
    removeParam(MEETING_RECAP_TOUR.STEP_ONE)
  }, [markMeetingRecapAsViewed, removeParam, track])

  const meetingRecapTourSteps = useMemo(() => ([
    {
      ref: buttonRef,
      title: I18n.t('publisher.meeting_recap.spotlight.step1.heading'),
      text: I18n.t('publisher.meeting_recap.spotlight.step1.body', { assistant_name: 'Callie' }),
    },
  ]), [])

  const meetingRecapTourNextButton = useMemo(() => (
    <Button>
      {I18n.t('publisher.action.next')}
    </Button>
  ), [])

  const toggleAiChatRail = () => {
    const recapPaneProps = mergeRecapPanePropsFromPathname(
      pathname,
      current?.paneProps ?? {},
      isRecapIntegrationEnabled,
    )
    const shouldAttachRecapContext = isAttachRecapContextEnabled(recapPaneProps.attachRecapContext)
      && !isAttachRecapContextEnabled(current?.paneProps?.attachRecapContext)

    if (current?.pane === 'ai_chat' && shouldAttachRecapContext) {
      track({
        name: TRACKING_EVENT_NAMES.NAVIGATION_LINK_CLICKED,
        properties: {
          navigation_link: 'ai_assistant',
          navigation_location: AI_CHAT_DISCOVERY_NAVIGATION_LOCATION_TOP_RIGHT,
          source_url: window.location.href,
        },
      })
      openAiChatRailPane(openOrReplace, recapPaneProps)
      return
    }

    if (current?.pane === 'ai_chat') {
      clearMeetingsSelectionFromUrlIfPresent(dispatch, search)
      clearCallieOnboardingFromUrlIfPresent(dispatch, search)
      closeAiChatRailPane(close, current)
    } else {
      track({
        name: TRACKING_EVENT_NAMES.NAVIGATION_LINK_CLICKED,
        properties: {
          navigation_link: 'ai_assistant',
          navigation_location: AI_CHAT_DISCOVERY_NAVIGATION_LOCATION_TOP_RIGHT,
          source_url: window.location.href,
        },
      })
      openAiChatRailPane(openOrReplace, recapPaneProps)
    }
  }

  const label = labelVariant === 'app_bar'
    ? I18n.t('publisher.app_shell.app_bar.ask_assistant', { assistant_name: 'Callie' })
    : I18n.t('publisher.agent_hub.discovery_fab.string_label')

  if (!functionalCookiesAllowed) return null

  return (
    <>
      <Button
        ref={buttonRef}
        aiLeadingIcon={CalendlyCallie}
        decoration="ai"
        size={size}
        onClick={toggleAiChatRail}
        data-testid={dataTestId}
      >
        {label}
      </Button>
      <Spotlight
        flowId={SPOTLIGHT_TOUR_FLOW_ID.MEETING_RECAP_TOUR_STEP_1}
        shouldShow={showMeetingRecapTourStep1}
        priority={SPOTLIGHT_TOUR_PRIORITY.MEETING_RECAP_TOUR}
        placement="bottom"
        steps={meetingRecapTourSteps}
        customCompleteButton={meetingRecapTourNextButton}
        onComplete={handleTourContinue}
        onExit={handleTourDismiss}
      />
    </>
  )
}
